import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Bot, User } from 'lucide-react';
import { cn } from '../../utils';

export interface MentionParticipant {
  id: string;
  name: string;
  type?: 'user' | 'agent';
  avatar?: string;
}

interface MentionInputProps {
  value: string;
  onChangeValue: (value: string) => void;
  participants?: MentionParticipant[];
  placeholder?: string;
  disabled?: boolean;
  maxRows?: number;
  className?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  onPaste?: React.ClipboardEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
}

export const MentionInput = forwardRef<HTMLTextAreaElement, MentionInputProps>(function MentionInput(
  {
    value,
    onChangeValue,
    participants = [],
    placeholder,
    disabled = false,
    maxRows = 5,
    className,
    onKeyDown,
    onPaste,
    onFocus,
  },
  forwardedRef,
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionTriggerPos, setMentionTriggerPos] = useState(-1);
  const [cursorPosition, setCursorPosition] = useState(0);

  const setRefs = useCallback((node: HTMLTextAreaElement | null) => {
    textareaRef.current = node;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  }, [forwardedRef]);

  const filteredParticipants = useMemo(
    () => participants.filter((participant) => participant.name.toLowerCase().includes(mentionFilter.toLowerCase())),
    [mentionFilter, participants],
  );

  useEffect(() => {
    if (mentionIndex >= filteredParticipants.length) {
      setMentionIndex(0);
    }
  }, [filteredParticipants.length, mentionIndex]);

  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const closeDropdown = useCallback(() => {
    setShowDropdown(false);
    setMentionFilter('');
    setMentionTriggerPos(-1);
    setMentionIndex(0);
  }, []);

  const updateMentionState = useCallback((nextValue: string, nextCursorPosition: number) => {
    const textBeforeCursor = nextValue.slice(0, nextCursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex >= 0) {
      const characterBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (lastAtIndex === 0 || /\s/.test(characterBeforeAt)) {
        const nextFilter = textBeforeCursor.slice(lastAtIndex + 1);
        if (!nextFilter.includes(' ')) {
          setMentionFilter(nextFilter);
          setMentionTriggerPos(lastAtIndex);
          setMentionIndex(0);
          setShowDropdown(participants.length > 0);
          return;
        }
      }
    }

    closeDropdown();
  }, [closeDropdown, participants.length]);

  const insertMention = useCallback((participant: MentionParticipant) => {
    const textarea = textareaRef.current;
    if (!textarea || mentionTriggerPos < 0) return;

    const selectionStart = textarea.selectionStart ?? cursorPosition;
    const beforeMention = value.slice(0, mentionTriggerPos);
    const afterMention = value.slice(selectionStart);
    const nextValue = `${beforeMention}@${participant.name} ${afterMention}`;
    const nextCaretPosition = beforeMention.length + participant.name.length + 2;

    onChangeValue(nextValue);
    closeDropdown();

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextCaretPosition, nextCaretPosition);
      setCursorPosition(nextCaretPosition);
    });
  }, [closeDropdown, cursorPosition, mentionTriggerPos, onChangeValue, value]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    const nextCursorPosition = event.target.selectionStart ?? nextValue.length;

    onChangeValue(nextValue);
    setCursorPosition(nextCursorPosition);
    updateMentionState(nextValue, nextCursorPosition);
  };

  const handleKeyDownInternal = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showDropdown && filteredParticipants.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionIndex((current) => (current + 1) % filteredParticipants.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionIndex((current) => (current - 1 + filteredParticipants.length) % filteredParticipants.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const selectedParticipant = filteredParticipants[mentionIndex] || filteredParticipants[0];
        if (selectedParticipant) {
          insertMention(selectedParticipant);
        }
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown();
        return;
      }
    }

    onKeyDown?.(event);
  };

  return (
    <div className={cn('relative flex-1', className)}>
      {showDropdown && filteredParticipants.length > 0 ? (
        <div
          ref={dropdownRef}
          className="absolute bottom-full left-0 right-0 z-20 mb-2 overflow-hidden rounded-[1.25rem] border border-border bg-card shadow-xl shadow-black/10"
        >
          <div className="border-b border-border bg-background/80 px-3 py-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            Mention an agent
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filteredParticipants.map((participant, index) => {
              const isAgent = participant.type !== 'user';
              const isActive = index === mentionIndex;
              return (
                <button
                  key={participant.id}
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertMention(participant);
                  }}
                  onMouseEnter={() => setMentionIndex(index)}
                  className={cn(
                    'flex min-h-[48px] w-full items-center gap-3 px-3 py-2 text-left transition-colors duration-150',
                    isActive ? 'bg-background' : 'hover:bg-background/80',
                  )}
                >
                  <div className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white',
                    isAgent ? 'bg-emerald-600/90' : 'bg-blue-600/90',
                  )}>
                    {participant.avatar ? (
                      <span className="text-sm font-semibold">{participant.avatar.slice(0, 1).toUpperCase()}</span>
                    ) : isAgent ? (
                      <Bot className="h-4 w-4" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{participant.name}</div>
                    <div className="text-xs text-muted-foreground">{isAgent ? 'Agent' : 'User'}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="border-t border-border bg-background/80 px-3 py-1.5 text-[10px] text-muted-foreground">
            ↑↓ to navigate · Enter or Tab to mention
          </div>
        </div>
      ) : null}

      <TextareaAutosize
        ref={setRefs}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDownInternal}
        onPaste={onPaste}
        onFocus={onFocus}
        onClick={(event) => {
          setCursorPosition(event.currentTarget.selectionStart ?? value.length);
        }}
        onSelect={(event) => {
          setCursorPosition(event.currentTarget.selectionStart ?? value.length);
        }}
        placeholder={placeholder}
        disabled={disabled}
        maxRows={maxRows}
        className={cn(
          'flex-1 resize-none border-none bg-transparent px-2 py-3 text-base text-foreground outline-none placeholder:text-muted-foreground',
          'focus:border-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed',
        )}
      />
    </div>
  );
});

export default MentionInput;
